import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, CircleAlert, Wrench } from "lucide-react";

import { AppShell } from "@/components/AppShell";
import { StatusPill } from "@/components/Progress";
import { api } from "@/lib/api-client";
import { useLocale } from "@/lib/locale";
import { cn } from "@/lib/utils";
import type { Intervention } from "@/lib/domain";

const TYPE_LABEL: Record<string, Record<"it" | "en" | "es", string>> = {
  report: { it: "Segnalazione", en: "Report", es: "Aviso" },
  inspection: { it: "Ispezione", en: "Inspection", es: "Inspección" },
  repair: { it: "Riparazione", en: "Repair", es: "Reparación" },
  warranty_claim: {
    it: "Richiesta in garanzia",
    en: "Warranty claim",
    es: "Reclamación de garantía",
  },
  renewal: { it: "Rinnovo", en: "Renewal", es: "Renovación" },
  sample_request: {
    it: "Richiesta campioni",
    en: "Sample request",
    es: "Solicitud de muestras",
  },
  other: { it: "Altro", en: "Other", es: "Otro" },
};

const STATUS_LABEL_I: Record<string, Record<"it" | "en" | "es", string>> = {
  open: { it: "Aperta", en: "Open", es: "Abierta" },
  in_progress: { it: "In carico", en: "In progress", es: "En curso" },
  closed: { it: "Chiusa", en: "Closed", es: "Cerrada" },
};

type Tab = "open" | "all" | "closed";

export default function InterventionsPage() {
  const { locale } = useLocale();
  const [tab, setTab] = useState<Tab>("open");
  const [rows, setRows] = useState<Intervention[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setRows(null);
    try {
      const all = await api.interventions();
      setRows(
        tab === "all"
          ? all
          : tab === "open"
            ? all.filter((i) => i.status === "open" || i.status === "in_progress")
            : all.filter((i) => i.status === "closed"),
      );
    } catch {
      setRows([]);
    }
  }, [tab]);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(id: string, dto: Record<string, unknown>) {
    setBusyId(id);
    try {
      await api.updateIntervention(id, dto);
      await load();
    } finally {
      setBusyId(null);
    }
  }

  const tabs: Array<{ id: Tab; label: string }> = [
    { id: "open", label: locale === "it" ? "Da gestire" : "To handle" },
    { id: "all", label: locale === "it" ? "Tutte" : "All" },
    { id: "closed", label: locale === "it" ? "Chiuse" : "Closed" },
  ];

  return (
    <AppShell>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="brand-kicker">
            {locale === "it" ? "Assistenza" : "After-sales"}
          </div>
          <h1 className="font-serif text-2xl text-paper sm:text-3xl">
            {locale === "it" ? "Interventi e garanzie" : "Interventions & claims"}
          </h1>
        </div>
        <div className="flex gap-1 rounded-full border border-white/15 p-0.5">
          {tabs.map((tb) => (
            <button
              key={tb.id}
              type="button"
              onClick={() => setTab(tb.id)}
              className={cn(
                "rounded-full px-3 py-1.5 text-[0.62rem] font-semibold uppercase tracking-[0.14em] transition",
                tab === tb.id ? "bg-white/15 text-paper" : "text-ice/60",
              )}
            >
              {tb.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6 grid gap-3">
        {rows === null && <p className="text-sm text-ice/60">Caricamento…</p>}
        {rows?.length === 0 && (
          <p className="text-sm text-ice/60">
            {locale === "it" ? "Nessun intervento." : "No interventions."}
          </p>
        )}

        {rows?.map((i) => (
          <article key={i.id} className="glass-panel-strong p-4">
            <div className="flex flex-wrap items-start gap-3">
              <Wrench className="mt-1 h-4 w-4 shrink-0 text-goldbright" />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-serif text-[0.95rem] text-paper">
                    {TYPE_LABEL[i.type]?.[locale] ?? i.type}
                  </span>
                  <StatusPill
                    status={i.status === "closed" ? "approved" : "reported"}
                    label={STATUS_LABEL_I[i.status]?.[locale] ?? i.status}
                  />
                  {i.claimStatus && (
                    <StatusPill
                      status={
                        i.claimStatus === "approved"
                          ? "approved"
                          : i.claimStatus === "rejected"
                            ? "issue"
                            : "in_progress"
                      }
                      label={`${locale === "it" ? "Garanzia" : "Claim"}: ${
                        i.claimStatus
                      }`}
                    />
                  )}
                  {i.warrantyItem && (
                    <span className="text-[0.62rem] text-ice/50">
                      {locale === "it" ? "copertura" : "coverage"}{" "}
                      {i.warrantyItem.kind} · {i.warrantyItem.years}
                      {locale === "it" ? " anni" : " yrs"}
                    </span>
                  )}
                </div>

                <p className="mt-1.5 text-[0.82rem] leading-relaxed text-paper/90">
                  {i.description}
                </p>

                <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1 text-[0.66rem] text-ice/55">
                  {i.surface ? (
                    <>
                      <Link
                        to={`/surfaces/${i.surface.id}`}
                        className="hover:text-paper"
                      >
                        {i.surface.name}
                        {i.surface.serial ? ` · ${i.surface.serial}` : ""}
                      </Link>
                      <span>
                        {i.surface.project.name} · {i.surface.project.country}
                      </span>
                    </>
                  ) : (
                    <span>
                      {locale === "it"
                        ? "Richiesta pre-vendita"
                        : locale === "es"
                          ? "Solicitud previa a la venta"
                          : "Pre-sales request"}
                    </span>
                  )}
                  <span className="num">
                    {i.requestedAt.slice(0, 10)}
                  </span>
                  {i.contactName && (
                    <span>
                      {i.contactName}
                      {i.contactEmail ? ` · ${i.contactEmail}` : ""}
                    </span>
                  )}
                </div>

                {i.details && (
                  <div className="mt-2 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-[0.7rem] text-ice/75">
                    {i.details.finishes && i.details.finishes.length > 0 && (
                      <div>
                        {locale === "it"
                          ? "Finiture"
                          : locale === "es"
                            ? "Acabados"
                            : "Finishes"}
                        : {i.details.finishes.join(", ")}
                      </div>
                    )}
                    {i.details.areaM2 ? <div>≈ {i.details.areaM2} m²</div> : null}
                    {i.details.studio && <div>{i.details.studio}</div>}
                    {i.details.phone && <div className="num">{i.details.phone}</div>}
                    {i.details.shippingAddress && (
                      <div>
                        {locale === "it"
                          ? "Spedizione"
                          : locale === "es"
                            ? "Envío"
                            : "Ship to"}
                        : {i.details.shippingAddress}
                      </div>
                    )}
                    {i.details.notes && <div>{i.details.notes}</div>}
                  </div>
                )}

                {i.resolution && (
                  <p className="mt-1.5 text-[0.74rem] text-green">
                    {locale === "it" ? "Esito" : "Outcome"}: {i.resolution}
                  </p>
                )}

                <div className="mt-3 flex flex-wrap gap-2">
                  {i.status !== "closed" && (
                    <>
                      {i.status === "open" && (
                        <button
                          type="button"
                          disabled={busyId === i.id}
                          onClick={() => act(i.id, { status: "in_progress" })}
                          className="rounded-full border border-gold/50 bg-gold/10 px-3 py-1.5 text-[0.58rem] font-semibold uppercase tracking-[0.12em] text-goldbright disabled:opacity-50"
                        >
                          {locale === "it" ? "Prendi in carico" : "Take on"}
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={busyId === i.id}
                        onClick={() => {
                          const resolution = window.prompt(
                            locale === "it" ? "Esito / risoluzione" : "Outcome / resolution",
                          );
                          if (resolution && resolution.length >= 3) {
                            void act(i.id, { status: "closed", resolution });
                          }
                        }}
                        className="inline-flex items-center gap-1.5 rounded-full border border-green/50 bg-green/10 px-3 py-1.5 text-[0.58rem] font-semibold uppercase tracking-[0.12em] text-green disabled:opacity-50"
                      >
                        <Check className="h-3 w-3" />
                        {locale === "it" ? "Chiudi" : "Close"}
                      </button>
                    </>
                  )}

                  {i.type === "warranty_claim" && i.claimStatus !== "approved" && (
                    <button
                      type="button"
                      disabled={busyId === i.id}
                      onClick={() => act(i.id, { claimStatus: "approved" })}
                      className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-[0.58rem] font-semibold uppercase tracking-[0.12em] text-ice/75 disabled:opacity-50"
                    >
                      <Check className="h-3 w-3" />
                      {locale === "it" ? "Garanzia ok" : "Approve claim"}
                    </button>
                  )}
                  {i.type === "warranty_claim" && i.claimStatus !== "rejected" && (
                    <button
                      type="button"
                      disabled={busyId === i.id}
                      onClick={() => act(i.id, { claimStatus: "rejected" })}
                      className="inline-flex items-center gap-1.5 rounded-full border border-rose/60 px-3 py-1.5 text-[0.58rem] font-semibold uppercase tracking-[0.12em] text-pink disabled:opacity-50"
                    >
                      <CircleAlert className="h-3 w-3" />
                      {locale === "it" ? "Rifiuta" : "Reject"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </article>
        ))}
      </div>
    </AppShell>
  );
}
